/**
 * R34.14 — First Setup Wizard (refactor "Onboarding Easy").
 *
 * Obiettivo: ridurre attrito iniziale per i clienti DEMO.
 * Sara feedback: "settore confonde, troppi checkbox, NC false su giorni chiusi".
 *
 * Flusso (5 step):
 *   0. Anagrafica essenziale (Ragione sociale + Città + Telefono)
 *   1. Attrezzature — domande numeriche +/- (no checkbox list confusa)
 *   2. Ambienti — 4 più comuni pre-selezionati, toggle SI/NO
 *   3. Giorni di chiusura — risolve il problema NC false su lunedì chiuso
 *   4. Done + Quick-start (3 CTA grandi: Ricevimento · Etichetta · Temperatura)
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sparkles, Building2, ArrowRight, ChevronLeft,
  Check, Loader2, Download, Refrigerator, Snowflake, Flame, Plus, Minus,
  Package, Tag, Thermometer, CalendarOff, ChefHat, Coffee, Bath, Boxes, Sofa,
  Cake, ShoppingBag, Utensils, Beef, Fish, Wheat, Store, Clock,
} from "lucide-react";
import { PRESET_TURNI_V2 } from "@/lib/turni";

const LS_KEY = "haccp_first_setup_draft_v2";
const LS_SKIP_KEY = "haccp_first_setup_skipped";

// R34.17 — Settori visuali (chip cards, NO dropdown).
// Pre-selezione "Ristorante" come default più comune.
const SECTOR_ICONS = {
  "Ristorante": ChefHat,
  "Pizzeria": Flame,
  "Bar": Coffee,
  "Pasticceria": Cake,
  "Gastronomia": ShoppingBag,
  "Tavola calda": Utensils,
  "Macelleria": Beef,
  "Pescheria": Fish,
  "Panificio": Wheat,
  "Altro": Store,
};

// Mapping domande numeriche → catalog backend keys (1 a 1).
const ATTREZZ_QUESTIONS = [
  { key: "frigo_cucina", label: "Quanti frigoriferi hai?", hint: "Frigo cucina, antipasti, ecc.", icon: Refrigerator, color: "#2563EB" },
  { key: "freezer_cucina", label: "Quanti freezer / congelatori?", hint: "Anche abbattitori e celle freezer", icon: Snowflake, color: "#0891B2" },
  { key: "vetrina_espositiva", label: "Banchi o vetrine refrigerate?", hint: "Vetrine espositive, banchi bar", icon: Package, color: "#7E22CE" },
  { key: "cottura_carni", label: "Forni o piastre da monitorare?", hint: "Cottura carni, forno pizza", icon: Flame, color: "#DC2626" },
];

// 4 ambienti pre-selezionati di default (cucina, sala, bagni, magazzino).
// Gli altri restano disponibili come "Aggiungi altri ambienti".
const AMBIENTI_DEFAULT = ["cucina", "sala", "bagni_clienti", "magazzino"];

const AMBIENTI_ICONS = {
  cucina: ChefHat,
  sala: Sofa,
  bar_banco: Coffee,
  bagni_clienti: Bath,
  bagni_staff: Bath,
  spogliatoio: Sofa,
  magazzino: Boxes,
  cella_frigo_pulizia: Refrigerator,
  esterni: Sofa,
};

const GIORNI = [
  { idx: 0, label: "Lun" },
  { idx: 1, label: "Mar" },
  { idx: 2, label: "Mer" },
  { idx: 3, label: "Gio" },
  { idx: 4, label: "Ven" },
  { idx: 5, label: "Sab" },
  { idx: 6, label: "Dom" },
];

const DEFAULT_FORM = {
  ragione_sociale: "",
  citta: "",
  telefono: "",
  partita_iva: "",
  indirizzo: "",
  // R34.17: settore selezionato visualmente (default Ristorante)
  settore: "Ristorante",
  // attrezzatura_counts: { frigo_cucina: 2, freezer_cucina: 1, vetrina_espositiva: 0, cottura_carni: 0 }
  attr_counts: { frigo_cucina: 1, freezer_cucina: 1, vetrina_espositiva: 0, cottura_carni: 0 },
  // ambienti come Set di key
  ambienti: { cucina: true, sala: true, bagni_clienti: true, magazzino: true },
  // giorni di chiusura settimanale: array di indici 0..6
  giorni_riposo: [],
  // R54.0 — numero di turni (1, 2 o 3). Il preset scelto viene salvato via /orgs/me/turni-config-v2
  turni_count: 2,
};

export default function FirstSetupWizard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [catalog, setCatalog] = useState({ ambienti: [] });
  const [form, setForm] = useState(DEFAULT_FORM);
  const [result, setResult] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // ── Mount: check status + load catalog ──────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      if (!user || user.role !== "admin") { setLoading(false); return; }
      // R60.13 — Il wizard NON è più saltabile ("skip forever" rimosso).
      // Il flag LS_SKIP_KEY residuo viene ignorato/ripulito.
      try { window.localStorage.removeItem(LS_SKIP_KEY); } catch { /* noop */ }
      try {
        const { data: st } = await api.get("/first-setup/status");
        if (cancelled) return;
        if (!st.applicable || st.completed) { setLoading(false); return; }
        const { data: cat } = await api.get("/first-setup/catalog");
        if (cancelled) return;
        setCatalog(cat);

        let draft = null;
        try {
          const raw = window.localStorage.getItem(LS_KEY);
          if (raw) draft = JSON.parse(raw);
        } catch { draft = null; }
        const prefilledAmbienti = { ...DEFAULT_FORM.ambienti };
        const merged = {
          ...DEFAULT_FORM,
          ragione_sociale: st.prefill?.ragione_sociale || "",
          partita_iva: st.prefill?.partita_iva || "",
          indirizzo: st.prefill?.indirizzo || "",
          citta: st.prefill?.citta || "",
          telefono: st.prefill?.telefono || "",
          settore: st.prefill?.settore || "Ristorante",
          giorni_riposo: Array.isArray(st.prefill?.giorni_riposo) ? st.prefill.giorni_riposo : [],
          ambienti: prefilledAmbienti,
          ...draft,
        };
        setForm(merged);
        setOpen(true);
      } catch (e) {
        console.warn("[first-setup] init failed:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    init();
    return () => { cancelled = true; };
  }, [user]);

  // ── Autosave bozza ──────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    try { window.localStorage.setItem(LS_KEY, JSON.stringify(form)); } catch { /* ignore */ }
  }, [form, open]);

  // ── Helpers ─────────────────────────────────────────────────────────
  const setAttrCount = (key, n) => {
    const clamped = Math.max(0, Math.min(20, n));
    setForm((f) => ({ ...f, attr_counts: { ...f.attr_counts, [key]: clamped } }));
  };

  const toggleAmb = (key) => {
    setForm((f) => {
      const cur = { ...f.ambienti };
      if (cur[key]) delete cur[key]; else cur[key] = true;
      return { ...f, ambienti: cur };
    });
  };

  const toggleGiornoRiposo = (idx) => {
    setForm((f) => {
      const cur = new Set(f.giorni_riposo);
      if (cur.has(idx)) cur.delete(idx); else cur.add(idx);
      return { ...f, giorni_riposo: Array.from(cur).sort() };
    });
  };

  // ── Validation per step ─────────────────────────────────────────────
  const stepValid = (() => {
    if (step === 0) return form.ragione_sociale.trim().length >= 2;
    if (step === 1) {
      const total = Object.values(form.attr_counts).reduce((s, v) => s + (v || 0), 0);
      return total >= 1; // almeno 1 attrezzatura
    }
    if (step === 2) return Object.keys(form.ambienti).length >= 1;
    if (step === 3) return true; // giorni chiusura sempre valido
    if (step === 4) return [1, 2, 3].includes(form.turni_count); // R54.0 — turni
    return true;
  })();

  // ── Submit ──────────────────────────────────────────────────────────
  const submit = async () => {
    setBusy(true);
    try {
      // Mappa attr_counts → payload backend (escludi count=0)
      const attrezzature = Object.entries(form.attr_counts)
        .filter(([_, c]) => (c || 0) > 0)
        .map(([key, count]) => ({ key, count }));
      const payload = {
        ragione_sociale: form.ragione_sociale.trim(),
        partita_iva: form.partita_iva.trim(),
        indirizzo: form.indirizzo.trim(),
        citta: form.citta.trim(),
        telefono: form.telefono.trim(),
        settore: form.settore || "Ristorante", // R34.17: settore visuale dal wizard
        n_coperti: null,
        n_dipendenti: null,
        attrezzature,
        ambienti: Object.keys(form.ambienti),
        giorni_riposo: form.giorni_riposo,
      };
      const { data } = await api.post("/first-setup/complete", payload);
      // R54.0 — Salva anche la configurazione turni scelta (best-effort)
      try {
        const turni = PRESET_TURNI_V2[form.turni_count] || PRESET_TURNI_V2[2];
        await api.put("/orgs/me/turni-config-v2", { turni });
        try { window.__TURNI__ = turni; } catch { /* noop */ }
      } catch (e) {
        console.warn("[first-setup] turni save failed:", e);
      }
      setResult(data);
      setStep(5);
      try { window.localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
      toast.success(`Configurato! ${data.inserted_temperature} controlli temperatura · ${data.inserted_pulizie} pulizie`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel completamento del setup");
    } finally {
      setBusy(false);
    }
  };

  const downloadManuale = async () => {
    setDownloading(true);
    try {
      const resp = await api.get("/first-setup/manuale-operatore.pdf", { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Manuale_Operatore_${form.ragione_sociale.replace(/\s+/g, "_").slice(0, 40)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore download manuale");
    } finally {
      setDownloading(false);
    }
  };

  const finishTo = (target) => {
    setOpen(false);
    nav(target);
  };

  if (loading || !open) return null;

  const totalSteps = 5;
  // R60.36 — SA in "Vedi come": può saltare il wizard obbligatorio per navigare nell'org.
  // Lo skip è per la sessione corrente (non completa il setup lato server: l'admin
  // vedrà ancora il wizard al suo primo login).
  const isSaImpersonating = !!user?.impersonating && (user?.role === "admin");
  const handleSaSkip = () => {
    setOpen(false);
    toast.info("Setup saltato (solo per la tua sessione SA). L'admin lo vedrà al primo login.", { duration: 3500 });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && step !== 5) return; setOpen(false); }}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden gap-0 [&>button]:hidden" data-testid="first-setup-wizard">
        {/* HEADER */}
        <div className="bg-gradient-to-br from-amber-50 to-white p-5 sm:p-6 border-b border-amber-100">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0" style={{ background: "var(--gold-soft)" }}>
                <Sparkles className="w-5 h-5" style={{ color: "var(--gold-hover)" }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">
                  {step === 5 ? "Tutto pronto!" : `Configurazione · ${step + 1}/${totalSteps}`}
                </p>
                <DialogTitle className="font-display text-lg sm:text-xl font-bold mt-0.5">
                  {step === 0 && "Iniziamo da te"}
                  {step === 1 && "La tua attività"}
                  {step === 2 && "Quali ambienti pulisci?"}
                  {step === 3 && "Quando sei chiuso?"}
                  {step === 4 && "Quanti turni?"}
                  {step === 5 && "Setup completato 🎉"}
                </DialogTitle>
              </div>
              {isSaImpersonating && step !== 5 && (
                <button
                  type="button"
                  onClick={handleSaSkip}
                  className="shrink-0 flex items-center gap-1 px-3 h-8 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 text-xs font-semibold"
                  data-testid="sa-skip-first-setup"
                  title="Salta il wizard (solo per te SA). L'admin lo vedrà al suo primo login."
                >
                  Salta (SA) →
                </button>
              )}
            </div>
          </DialogHeader>
          <div className="flex items-center gap-1.5 mt-4">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${i === step ? "w-10 bg-amber-500" : i < step ? "w-3 bg-amber-300" : "w-3 bg-slate-200"}`}
              />
            ))}
          </div>
        </div>

        {/* BODY */}
        <div className="p-5 sm:p-6 max-h-[60vh] overflow-y-auto">
          {/* ── Step 0: Anagrafica essenziale ─────────────────────── */}
          {step === 0 && (
            <div className="space-y-4" data-testid="first-setup-step-0">
              <p className="text-sm text-slate-600">
                Bastano 3 dati per personalizzare la tua app. Tutto il resto puoi aggiungerlo dopo.
              </p>
              <div>
                <label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                  <Building2 className="w-3.5 h-3.5" /> Nome dell&apos;attività <span className="text-rose-600">*</span>
                </label>
                <Input
                  value={form.ragione_sociale}
                  onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })}
                  placeholder="Es. Pizzeria Da Mario"
                  className="mt-1"
                  data-testid="first-setup-ragione-sociale"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700">Città</label>
                  <Input value={form.citta} onChange={(e) => setForm({ ...form, citta: e.target.value })} placeholder="Milano" className="mt-1" data-testid="first-setup-citta" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Telefono</label>
                  <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="06 12345678" className="mt-1" data-testid="first-setup-telefono" />
                </div>
              </div>
              <details className="mt-2 text-xs">
                <summary className="text-slate-500 cursor-pointer hover:text-slate-700">Dati avanzati (opzionali) — P.IVA, indirizzo</summary>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Partita IVA</label>
                    <Input value={form.partita_iva} onChange={(e) => setForm({ ...form, partita_iva: e.target.value })} placeholder="IT12345678901" className="mt-1" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Indirizzo</label>
                    <Input value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} placeholder="Via Roma 10" className="mt-1" />
                  </div>
                </div>
              </details>
            </div>
          )}

          {/* ── Step 1: Settore + Attrezzature numeriche ───────────── */}
          {step === 1 && (
            <div className="space-y-4" data-testid="first-setup-step-1">
              {/* Settore picker visuale (chip cards) */}
              <div>
                <p className="text-sm font-semibold text-slate-800 mb-2">Che tipo di attività hai?</p>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {(catalog.sectors || []).map((s) => {
                    const Icon = SECTOR_ICONS[s.key] || Store;
                    const selected = form.settore === s.key;
                    return (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, settore: s.key }))}
                        className={`flex flex-col items-center gap-1.5 rounded-lg border-2 p-2.5 transition-all ${
                          selected
                            ? "border-amber-500 bg-amber-50 shadow-sm"
                            : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                        data-testid={`first-setup-sector-${s.key.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <Icon className={`w-5 h-5 ${selected ? "text-amber-700" : "text-slate-600"}`} />
                        <span className={`text-[10px] sm:text-[11px] leading-tight text-center ${selected ? "font-bold text-amber-900" : "text-slate-700"}`}>
                          {s.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-slate-500 italic mt-1.5">
                  In base al tuo settore aggiungo verifiche specifiche al tuo check-list giornaliero.
                </p>
              </div>

              <div className="border-t border-slate-200 pt-3">
                <p className="text-sm text-slate-600 mb-3">
                  <b>Dimmi quanti ne hai.</b> Creerò automaticamente i controlli temperatura con le soglie HACCP standard.
                </p>
              </div>

              {ATTREZZ_QUESTIONS.map((q) => {
                const Icon = q.icon;
                const count = form.attr_counts[q.key] || 0;
                return (
                  <div
                    key={q.key}
                    className={`rounded-lg border p-4 transition-all ${count > 0 ? "border-amber-400 bg-amber-50/60" : "border-slate-200 bg-white"}`}
                    data-testid={`first-setup-attr-${q.key}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${q.color}15` }}>
                        <Icon className="w-5 h-5" style={{ color: q.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm leading-tight">{q.label}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">{q.hint}</p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          type="button"
                          onClick={() => setAttrCount(q.key, count - 1)}
                          className="w-9 h-9 rounded border border-slate-300 bg-white flex items-center justify-center text-slate-700 hover:bg-slate-50 disabled:opacity-30"
                          disabled={count <= 0}
                          aria-label="Riduci"
                          data-testid={`first-setup-attr-minus-${q.key}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="font-bold text-lg w-8 text-center tabular-nums" data-testid={`first-setup-attr-count-${q.key}`}>{count}</span>
                        <button
                          type="button"
                          onClick={() => setAttrCount(q.key, count + 1)}
                          className="w-9 h-9 rounded border border-slate-300 bg-white flex items-center justify-center text-slate-700 hover:bg-slate-50"
                          aria-label="Aumenta"
                          data-testid={`first-setup-attr-plus-${q.key}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              <p className="text-[11px] text-slate-500 italic px-1">
                Puoi sempre aggiungere altre attrezzature dalla pagina Temperature.
              </p>
            </div>
          )}

          {/* ── Step 2: Ambienti ─────────────────────────────────── */}
          {step === 2 && (
            <div className="space-y-3" data-testid="first-setup-step-2">
              <p className="text-sm text-slate-600">
                Per ogni ambiente creo le pulizie standard con frequenze HACCP consigliate. <b>I 4 più comuni sono già selezionati.</b>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {catalog.ambienti.map((a) => {
                  const selected = !!form.ambienti[a.key];
                  const Icon = AMBIENTI_ICONS[a.key] || Boxes;
                  const isDefault = AMBIENTI_DEFAULT.includes(a.key);
                  return (
                    <button
                      key={a.key}
                      type="button"
                      onClick={() => toggleAmb(a.key)}
                      className={`flex items-center gap-3 rounded-lg border p-3 text-left transition-all ${selected ? "border-amber-400 bg-amber-50/60" : "border-slate-200 bg-white hover:border-slate-300"}`}
                      data-testid={`first-setup-amb-${a.key}`}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${selected ? "bg-amber-500 border-amber-500" : "border-slate-300 bg-white"}`}>
                        {selected && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                      </div>
                      <Icon className="w-4 h-4 text-amber-700 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm">{a.label}</p>
                        <p className="text-[11px] text-slate-500">
                          {a.task_count} pulizie standard{isDefault && " · consigliato"}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Step 3: Giorni di chiusura ────────────────────────── */}
          {step === 3 && (
            <div className="space-y-4" data-testid="first-setup-step-3">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
                <CalendarOff className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-blue-900">Non creeremo Non Conformità nei giorni di chiusura.</p>
                  <p className="text-xs text-blue-800 mt-1 leading-snug">
                    Se sei chiuso al lunedì, nessun controllo sarà richiesto quel giorno e nessun &quot;dimenticato&quot; verrà segnalato.
                  </p>
                </div>
              </div>
              <p className="text-sm text-slate-600">Quali giorni sei <b>chiuso</b>?</p>
              <div className="grid grid-cols-7 gap-2">
                {GIORNI.map((g) => {
                  const selected = form.giorni_riposo.includes(g.idx);
                  return (
                    <button
                      key={g.idx}
                      type="button"
                      onClick={() => toggleGiornoRiposo(g.idx)}
                      className={`h-14 rounded-lg border-2 flex flex-col items-center justify-center text-sm font-semibold transition-all ${
                        selected
                          ? "border-rose-500 bg-rose-50 text-rose-900"
                          : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                      }`}
                      data-testid={`first-setup-giorno-${g.idx}`}
                    >
                      <span className="text-[11px] uppercase tracking-wider opacity-70">{g.label}</span>
                      <span className="text-lg leading-none mt-0.5">{selected ? "✕" : "—"}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-500 italic">
                {form.giorni_riposo.length === 0
                  ? "Sei aperto 7 giorni su 7."
                  : `Chiuso ${form.giorni_riposo.length} ${form.giorni_riposo.length === 1 ? "giorno" : "giorni"} a settimana.`}
              </p>
            </div>
          )}

          {/* ── Step 4: Turni (R54.0) ─────────────────────────────── */}
          {step === 4 && (
            <div className="space-y-4" data-testid="first-setup-step-turni">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-indigo-50 border border-indigo-200">
                <Clock className="w-5 h-5 text-indigo-700 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-indigo-900">Turni di lavoro nella tua cucina.</p>
                  <p className="text-xs text-indigo-800 mt-1 leading-snug">
                    Alcuni controlli HACCP (es. temperature frigo) vanno fatti più volte al giorno.
                    Suddividiamo la giornata in <b>turni</b>, così il personale vede solo le task del proprio turno.
                  </p>
                </div>
              </div>
              <p className="text-sm text-slate-600 font-semibold">Quanti turni ha il tuo ristorante?</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { n: 1, label: "1 turno", subtitle: "Turno unico", detail: "6:00 → 24:00", hint: "Ideale per attività con orario ridotto (es. solo pranzo o solo cena)." },
                  { n: 2, label: "2 turni", subtitle: "Mattina / Sera", detail: "6:00 → 14:00 · 14:00 → 24:00", hint: "Il più comune per ristoranti tradizionali con pranzo e cena." },
                  { n: 3, label: "3 turni", subtitle: "Mattina / Pomerig. / Sera", detail: "6:00 → 12:00 · 12:00 → 18:00 · 18:00 → 24:00", hint: "Per attività continuate o ristorazione ad alto volume." },
                ].map((opt) => {
                  const selected = form.turni_count === opt.n;
                  return (
                    <button
                      key={opt.n}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, turni_count: opt.n }))}
                      className={`text-left rounded-lg border-2 p-3 transition-all ${
                        selected
                          ? "border-amber-500 bg-amber-50 shadow-sm"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                      data-testid={`first-setup-turni-${opt.n}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`w-6 h-6 rounded-full text-[11px] font-bold flex items-center justify-center ${selected ? "bg-amber-500 text-white" : "bg-slate-200 text-slate-700"}`}>
                          {opt.n}
                        </span>
                        <p className={`font-bold text-sm ${selected ? "text-amber-900" : "text-slate-800"}`}>{opt.label}</p>
                      </div>
                      <p className="text-[11px] text-slate-600">{opt.subtitle}</p>
                      <p className="text-[10px] font-mono text-slate-500 mt-1">{opt.detail}</p>
                      <p className="text-[10px] text-slate-500 mt-1.5 leading-snug italic">{opt.hint}</p>
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-500 italic">
                Puoi modificare orari e alert in ogni momento da <b>Impostazioni → Turni</b>.
              </p>
            </div>
          )}

          {/* ── Step 5: Done + Quick Start ────────────────────────── */}
          {step === 5 && result && (
            <div className="text-center py-2" data-testid="first-setup-step-done">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Check className="w-9 h-9 text-emerald-600" strokeWidth={3} />
              </div>
              <p className="font-display text-xl font-bold text-slate-900 mb-2">
                Bravo! Tutto pronto.
              </p>
              <p className="text-sm text-slate-600 max-w-md mx-auto leading-snug">
                Ho configurato <b>{result.inserted_temperature}</b> controlli temperatura e <b>{result.inserted_pulizie}</b> pulizie per <b>{result.ragione_sociale || form.ragione_sociale}</b>.
                {result.inserted_verifiche_settore > 0 && (
                  <span className="block mt-1 text-amber-700 text-xs">
                    ✨ Aggiunte <b>{result.inserted_verifiche_settore}</b> verifiche pre-operative specifiche per {result.settore}
                  </span>
                )}
              </p>

              {/* Quick-start CTAs */}
              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
                  Da dove vuoi iniziare?
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  <button
                    onClick={() => finishTo("/ricevimenti?new=1")}
                    className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 hover:bg-amber-100 transition-all text-left"
                    data-testid="quickstart-ricevimento"
                  >
                    <Package className="w-7 h-7 text-amber-700 mb-2" />
                    <p className="font-semibold text-sm leading-tight">Registra un ricevimento</p>
                    <p className="text-[11px] text-slate-600 mt-1">Scatta foto del DDT, l&apos;AI estrae tutto</p>
                  </button>
                  <button
                    onClick={() => finishTo("/etichette")}
                    className="rounded-lg border-2 border-blue-300 bg-blue-50 p-4 hover:bg-blue-100 transition-all text-left"
                    data-testid="quickstart-etichetta"
                  >
                    <Tag className="w-7 h-7 text-blue-700 mb-2" />
                    <p className="font-semibold text-sm leading-tight">Stampa la prima etichetta</p>
                    <p className="text-[11px] text-slate-600 mt-1">QR + scadenza in 3 secondi</p>
                  </button>
                  <button
                    onClick={() => finishTo("/temperature")}
                    className="rounded-lg border-2 border-rose-300 bg-rose-50 p-4 hover:bg-rose-100 transition-all text-left"
                    data-testid="quickstart-temperatura"
                  >
                    <Thermometer className="w-7 h-7 text-rose-700 mb-2" />
                    <p className="font-semibold text-sm leading-tight">Rileva una temperatura</p>
                    <p className="text-[11px] text-slate-600 mt-1">Foto del termometro → lettura AI</p>
                  </button>
                </div>
              </div>

              <div className="mt-5 p-3 rounded-lg bg-slate-50 border border-slate-200 max-w-md mx-auto">
                <Button
                  onClick={downloadManuale}
                  disabled={downloading}
                  variant="outline"
                  size="sm"
                  className="w-full"
                  data-testid="first-setup-download-manuale"
                >
                  {downloading
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Genero PDF…</>
                    : <><Download className="w-4 h-4 mr-2" /> Scarica il manuale operatore (PDF)</>}
                </Button>
                <p className="text-[10px] text-slate-500 mt-2 leading-snug">
                  Manuale rapido HACCP da consegnare al tuo personale (5 pagine).
                </p>
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="bg-slate-50 px-5 sm:px-6 py-3.5 border-t border-slate-200 flex items-center justify-between gap-3">
          {step === 0 && (
            <>
              <p className="text-xs text-slate-500 italic" data-testid="first-setup-mandatory-note">
                Setup obbligatorio · non skippabile
              </p>
              <Button onClick={() => setStep(1)} disabled={!stepValid || busy} className="btn-gold" data-testid="first-setup-next-0">
                Avanti <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}
          {step === 1 && (
            <>
              <Button variant="outline" onClick={() => setStep(0)} disabled={busy} data-testid="first-setup-back-1">
                <ChevronLeft className="w-4 h-4 mr-1" /> Indietro
              </Button>
              <Button onClick={() => setStep(2)} disabled={!stepValid || busy} className="btn-gold" data-testid="first-setup-next-1">
                Avanti <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)} disabled={busy} data-testid="first-setup-back-2">
                <ChevronLeft className="w-4 h-4 mr-1" /> Indietro
              </Button>
              <Button onClick={() => setStep(3)} disabled={!stepValid || busy} className="btn-gold" data-testid="first-setup-next-2">
                Avanti <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}
          {step === 3 && (
            <>
              <Button variant="outline" onClick={() => setStep(2)} disabled={busy} data-testid="first-setup-back-3">
                <ChevronLeft className="w-4 h-4 mr-1" /> Indietro
              </Button>
              <Button onClick={() => setStep(4)} disabled={!stepValid || busy} className="btn-gold" data-testid="first-setup-next-3">
                Avanti <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}
          {step === 4 && (
            <>
              <Button variant="outline" onClick={() => setStep(3)} disabled={busy} data-testid="first-setup-back-4">
                <ChevronLeft className="w-4 h-4 mr-1" /> Indietro
              </Button>
              <Button onClick={submit} disabled={!stepValid || busy} className="btn-gold" data-testid="first-setup-submit">
                {busy
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Configuro…</>
                  : <><Check className="w-4 h-4 mr-2" /> Crea tutto e inizia</>}
              </Button>
            </>
          )}
          {step === 5 && (
            <Button onClick={() => finishTo("/oggi")} variant="outline" className="w-full" data-testid="first-setup-finish">
              Salta, vai alla home <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
